import { Text } from "@mantine/core";
import {
  GoogleMap,
  InfoWindow,
  Marker,
} from "@react-google-maps/api";
import { IconMapPinFilled } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";

import { useGoogleMap } from "#/hook/useGoogleMap";

interface MapPoint {
  lat: number;
  lng: number;
  label?: string;
  category?: string;
  color?: string;
}

interface MapSpec {
  title?: string;
  zoom?: number;
  points: MapPoint[];
}

const PALETTE = [
  "#7C3AED",
  "#DB2777",
  "#2563EB",
  "#059669",
  "#D97706",
  "#DC2626",
  "#0891B2",
  "#9333EA",
  "#65A30D",
  "#E11D48",
];
const DEFAULT_COLOR = "#7C3AED";

const toCoord = (value: unknown): number | null => {
  const n =
    typeof value === "string"
      ? parseFloat(value)
      : typeof value === "number"
        ? value
        : NaN;
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const pinIcon = (color: string): string =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    ReactDOMServer.renderToStaticMarkup(
      <IconMapPinFilled color={color} size={34} />
    )
  )}`;

/**
 * Renders a Google Map with pins from the JSON the assistant emits in a
 * `tma-map` fenced code block. Pins are colored by `category` (with a legend)
 * and clickable for details. Reuses the app's global Maps loader. All
 * validation lives here so callers never need to guard.
 */
export const AssistantMap = ({ raw }: { raw: string }) => {
  const { isLoaded } = useGoogleMap();
  const [selected, setSelected] = useState<number | null>(null);
  const spec = parseSpec(raw);

  const categories = useMemo(() => {
    const list: string[] = [];
    spec?.points.forEach((p) => {
      if (p.category && !list.includes(p.category)) {
        list.push(p.category);
      }
    });
    return list;
  }, [spec]);

  const colorFor = useCallback(
    (point: MapPoint): string => {
      if (point.color) {
        return point.color;
      }
      if (point.category) {
        return PALETTE[categories.indexOf(point.category) % PALETTE.length];
      }
      return DEFAULT_COLOR;
    },
    [categories]
  );

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      if (!spec || spec.points.length === 0) {
        return;
      }
      if (spec.points.length === 1) {
        map.setCenter({ lat: spec.points[0].lat, lng: spec.points[0].lng });
        map.setZoom(spec.zoom ?? 14);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      spec.points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, 48);
    },
    [spec]
  );

  if (!spec) {
    return (
      <Text size="xs" c="dimmed" className="italic">
        (no mappable locations)
      </Text>
    );
  }

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
      {spec.title && (
        <Text size="sm" fw={600} className="px-3 pt-2 text-slate-700">
          {spec.title}
        </Text>
      )}

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2">
          {categories.map((category, i) => (
            <span key={category} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
              />
              <span className="text-xs text-slate-600">{category}</span>
            </span>
          ))}
        </div>
      )}

      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "18rem" }}
          onLoad={onLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {spec.points.map((p, i) => (
            <Marker
              key={`${p.lat},${p.lng},${i}`}
              position={{ lat: p.lat, lng: p.lng }}
              title={p.label}
              onClick={() => setSelected(i)}
              icon={{
                url: pinIcon(colorFor(p)),
                scaledSize: new google.maps.Size(30, 30),
                anchor: new google.maps.Point(15, 28),
              }}
            />
          ))}

          {selected !== null && spec.points[selected] && (
            <InfoWindow
              position={{
                lat: spec.points[selected].lat,
                lng: spec.points[selected].lng,
              }}
              onCloseClick={() => setSelected(null)}
            >
              <div className="text-xs">
                {spec.points[selected].label && (
                  <div className="font-semibold text-slate-800">
                    {spec.points[selected].label}
                  </div>
                )}
                {spec.points[selected].category && (
                  <div className="text-slate-500">
                    {spec.points[selected].category}
                  </div>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <div className="flex h-72 items-center justify-center text-sm text-slate-400">
          Loading map…
        </div>
      )}
    </div>
  );
};

const parseSpec = (raw: string): MapSpec | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }
  const spec = parsed as { title?: unknown; zoom?: unknown; points?: unknown };
  if (!Array.isArray(spec.points)) {
    return null;
  }

  const points: MapPoint[] = [];
  for (const entry of spec.points) {
    if (typeof entry !== "object" || entry === null) {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const lat = toCoord(record.lat ?? record.latitude);
    const lng = toCoord(record.lng ?? record.longitude);
    if (lat === null || lng === null) {
      continue;
    }
    points.push({
      lat,
      lng,
      label: typeof record.label === "string" ? record.label : undefined,
      category:
        typeof record.category === "string" ? record.category : undefined,
      color: typeof record.color === "string" ? record.color : undefined,
    });
  }

  if (points.length === 0) {
    return null;
  }

  return {
    title: typeof spec.title === "string" ? spec.title : undefined,
    zoom: typeof spec.zoom === "number" ? spec.zoom : undefined,
    points,
  };
};
